<template>
	<transition name="fade">
		<div
			v-if="loading"
			:class="['spinner-element', `spinner-element__color-theme-${colorTheme}`]"
		>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 66 66"
				class="svg_spinner"
				role="image"
				aria-label="Loading"
			>
				<circle
					fill="none"
					:stroke-width="Number(spinnerStrokeWidth)"
					stroke-linecap="round"
					cx="33"
					cy="33"
					r="30"
				/>
			</svg>
		</div>
	</transition>
</template>

<script>
/**
 * SpinnerSVG
 *
 * This component is 100% width by default. You need to wrap it into an html element and provide a width for it, otherwise it will take full width of the container its placed into.
 * ### Usage
 *
 * For Angular and Vue.js usage code snippets, click the `Show code` button at the bottom right of every story example.
 *
 */
export default {
	name: "SpinnerSVG",
	props: {
		/**
		 * When `true`, the Loading Indicator animation appears.
		 */
		loading: {
			type: Boolean,
			default: true
		},

		/**
		 * Set the stroke width of the spinner
		 */
		spinnerStrokeWidth: {
			type: Number,
			default: 4
		},

		/**
		 * Set color of the spinner.
		 * @values red, blue, white
		 */
		colorTheme: {
			type: String,
			default: "red"
		}
	}
};
</script>

<style lang="scss" scoped>
.spinner-element {
	display: block;
	width: 100%;
	overflow: hidden;

	.svg_spinner {
		display: block;
		width: 100%;
		animation: spinner-rotator 1.4s linear infinite;

		circle {
			stroke-dashoffset: 0;
			stroke-dasharray: 187;
			transform-origin: center;
			animation: spinner-dash 1.4s ease-in-out infinite,
				red-colors 5.6s ease-in-out infinite;
		}
	}

	&__color-theme {
		&-red {
			.svg_spinner {
				circle {
					animation: spinner-dash 1.4s ease-in-out infinite,
						red-colors 5.6s ease-in-out infinite;
				}
			}
		}

		&-blue {
			.svg_spinner {
				circle {
					animation: spinner-dash 1.4s ease-in-out infinite,
						blue-colors 5.6s ease-in-out infinite;
				}
			}
		}

		&-white {
			.svg_spinner {
				circle {
					animation: spinner-dash 1.4s ease-in-out infinite,
						white-colors 5.6s ease-in-out infinite;
				}
			}
		}
	}
}

@keyframes spinner-rotator {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(270deg);
	}
}

@keyframes spinner-dash {
	0% {
		stroke-dashoffset: 187;
		transform: rotate(0deg);
	}
	50% {
		stroke-dashoffset: 46.75;
		transform: rotate(135deg);
	}
	100% {
		stroke-dashoffset: 187;
		transform: rotate(450deg);
	}
}

@keyframes red-colors {
	0% {
		stroke: #c20029;
	}
	25% {
		stroke: #f6001e;
	}
	50% {
		stroke: #c20029;
	}
	75% {
		stroke: #670026;
	}
	100% {
		stroke: #c20029;
	}
}

@keyframes blue-colors {
	0% {
		stroke: $color--blue-4;
	}
	50% {
		stroke: $color--blue-5;
	}
	100% {
		stroke: $color--blue-4;
	}
}

@keyframes white-colors {
	0% {
		stroke: $color--neutral-10;
	}
	100% {
		stroke: $color--neutral-10;
	}
}
</style>
